import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { offlineCatalogAtom } from '@/lib/offline/catalog';
import { useOfflineEnabled } from '@/lib/offline/useOfflineEnabled';
import { cn } from '@/lib/utils';
import { useAtomValue } from 'jotai';
import { CloudAlert, CloudCheck, Loader2 } from 'lucide-react';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

interface OfflineIndicatorProps {
  projectId: string;
  /** Tailwind size class for the icon (default `size-3.5`). */
  className?: string;
}

/**
 * Small badge shown next to a project (sidebar item, breadcrumb) when that
 * project has been made available offline. Reads the persisted offline catalog
 * and renders nothing unless there is a relevant offline state.
 */
export const OfflineIndicator: FunctionComponent<OfflineIndicatorProps> = ({ projectId, className }) => {
  const { t } = useTranslation('project');
  const offlineEnabled = useOfflineEnabled();
  const catalog = useAtomValue(offlineCatalogAtom);
  const entry = catalog[projectId];
  if (!offlineEnabled || !entry) return null;

  const size = cn('size-3.5 shrink-0', className);

  let icon: React.ReactNode;
  let label: string;
  switch (entry.status) {
    case 'ready':
      icon = <CloudCheck className={cn(size, 'text-emerald-500')} />;
      label = t('offline.availableOfflineTooltip');
      break;
    case 'downloading':
      icon = <Loader2 className={cn(size, 'animate-spin text-blue-500')} />;
      label = t('offline.downloading');
      break;
    case 'partial':
      icon = <CloudAlert className={cn(size, 'text-amber-500')} />;
      label = t('offline.partialTooltip');
      break;
    case 'stale':
      icon = <CloudAlert className={cn(size, 'text-amber-500')} />;
      label = t('offline.staleTooltip');
      break;
    default:
      return null; // 'error' or unknown → no indicator
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='inline-flex items-center' aria-label={label}>
          {icon}
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
};
